import React, { useState } from "react";

interface BundleItem {
  key: string;
  label: string;
  stage: "pre" | "72";
  done: boolean;
  doneDate: string;
  doneBy: string;
}

interface Bundle {
  id: string;
  residentName: string;
  admissionDate: string;
  items: BundleItem[];
}

const KEY = "admissions_bundle";

const PRE = [
  "Funding confirmed & agreed rate",
  "Contract signed & consent obtained",
  "GP registration confirmed",
  "Pharmacy registered",
  "Room ready & first-night plan",
  "Pre-admission visit completed",
];

const H72 = [
  "Waterlow skin assessment",
  "Falls risk assessment",
  "MUST nutrition assessment",
  "Skin integrity check",
  "Mental health & cognition baseline",
  "Capacity / MCA screening",
  "Medication review & MAR started",
  "Personalised care plan created",
];

function load(): Bundle[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
}

function loadResidents(): any[] {
  try {
    return JSON.parse(localStorage.getItem("residents_records") || "[]");
  } catch {
    return [];
  }
}

const makeBundle = (name: string, admissionDate: string): Bundle => ({
  id: `adm-${Date.now()}`,
  residentName: name,
  admissionDate,
  items: [
    ...PRE.map((label, i) => ({ key: `pre-${i}`, label, stage: "pre" as const, done: false, doneDate: "", doneBy: "" })),
    ...H72.map((label, i) => ({ key: `72-${i}`, label, stage: "72" as const, done: false, doneDate: "", doneBy: "" })),
  ],
});

export default function AdmissionsBundle({ user }: { user?: any }) {
  const [bundles, setBundles] = useState<Bundle[]>(load);
  const residents = loadResidents();
  const [name, setName] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [openId, setOpenId] = useState("");
  const [message, setMessage] = useState("");

  const save = (next: Bundle[]) => {
    setBundles(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  };

  const start = () => {
    if (!name) {
      setMessage("Enter the resident's name.");
      return;
    }
    const b = makeBundle(name, date);
    save([b, ...bundles]);
    setOpenId(b.id);
    setName("");
    setMessage("Admission bundle started - complete every item.");
  };

  const mark = (bundleId: string, key: string) => {
    save(bundles.map(b => (b.id === bundleId ? { ...b, items: b.items.map(i => (i.key === key ? { ...i, done: !i.done, doneDate: !i.done ? new Date().toISOString().slice(0, 10) : "", doneBy: !i.done ? (user && user.name ? user.name : "") : "" } : i)) } : b)));
  };

  const remove = (id: string) => save(bundles.filter(b => b.id !== id));

  const hoursSince = (d: string) => (Date.now() - new Date(d).getTime()) / 36400000;

  const itemState = (b: Bundle, i: BundleItem) => {
    if (i.done) return "done";
    const limit = i.stage === "pre" ? 0 : 72;
    return hoursSince(b.admissionDate) > limit ? "overdue" : "due";
  };

  const progress = (b: Bundle) => Math.round((b.items.filter(i => i.done).length / b.items.length) * 100);

  const createResident = (b: Bundle) => {
    const existing = loadResidents();
    if (existing.some((r: any) => r.name === b.residentName)) {
      setMessage("Resident already exists in Residents & Invoices.");
      return;
    }
    const rec = { id: `res-${Date.now()}`, name: b.residentName, dob: "", admissionDate: b.admissionDate, exitDate: "", room: "", nhsNumber: "", gpName: "", gpPhone: "", familyName: "", familyPhone: "", familyEmail: "", address: "", relation: "", legalRep: "", dietary: "", allergies: "", mobility: "", notes: "Created from Admissions Bundle", fundingType: "Self-funding", council: "", totalFee: "", councilPays: "", residentPays: "" };
    const next = [rec, ...existing];
    localStorage.setItem("residents_records", JSON.stringify(next));
    setMessage("Resident record created. Now set funding & rate in Residents & Invoices, then Generate invoices.");
  };

  const open = bundles.find(b => b.id === openId);

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Admissions 72-Hour Bundle</h2>
        <p className="text-sm text-slate-600">Every admission tracked: pre-admission essentials plus the eight assessments that must be complete within 72 hours. Nothing missed, ever.</p>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 font-semibold text-slate-900">Start a new admission bundle</h3>
        <div className="flex flex-wrap gap-2">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Resident name" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <button onClick={start} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Start bundle</button>
        </div>
      </div>

      {open ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-semibold text-slate-900">{open.residentName} - admitted {open.admissionDate}</h3>
            <div className="text-sm font-semibold text-slate-900">{progress(open)}% complete</div>
          </div>
          <div className="mt-2 h-2 rounded bg-slate-100"><div className="h-2 rounded bg-green-600" style={{ width: `${progress(open)}%` }} /></div>
          <div className="mt-3 flex gap-2">
            <button onClick={() => createResident(open)} className="rounded-lg bg-blue-900 px-3 py-1 text-sm text-white">Create resident record</button>
            <button onClick={() => setOpenId("")} className="rounded-lg border border-slate-300 px-3 py-1 text-sm">Close</button>
          </div>
          {(["pre", "72"] as const).map(stage => (
            <div key={stage} className="mt-4">
              <h4 className="mb-2 text-sm font-semibold text-slate-700">{stage === "pre" ? "Before admission" : "Within 72 hours"}</h4>
              <div className="space-y-1">
                {open.items.filter(i => i.stage === stage).map(i => {
                  const st = itemState(open, i);
                  return (
                    <div key={i.key} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 p-2">
                      <div className="text-sm text-slate-800">{i.label}{i.done ? <span className="ml-2 text-xs text-green-700">done {i.doneDate} by {i.doneBy}</span> : null}</div>
                      <div className="flex items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${st === "done" ? "bg-green-100 text-green-700" : st === "overdue" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{st === "done" ? "Done" : st === "overdue" ? "OVERDUE" : "Due"}</span>
                        <button onClick={() => mark(open.id, i.key)} className="rounded-lg border border-slate-300 px-2 py-1 text-xs">{i.done ? "Undo" : "Mark done"}</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="space-y-2">
        {bundles.map(b => (
          <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="text-sm font-medium text-slate-900">{b.residentName} <span className="text-xs text-slate-500">admitted {b.admissionDate}</span></div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-semibold ${b.items.some(i => itemState(b, i) === "overdue") ? "text-red-700" : progress(b) === 100 ? "text-green-700" : "text-amber-700"}`}>{progress(b)}%{b.items.some(i => itemState(b, i) === "overdue") ? " - overdue items" : ""}</span>
              <button onClick={() => setOpenId(b.id)} className="rounded-lg border border-slate-300 px-2 py-1 text-xs">Open</button>
              <button onClick={() => remove(b.id)} className="text-xs text-red-600">Remove</button>
            </div>
          </div>
        ))}
        {bundles.length === 0 ? <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No admission bundles yet. Start one for every new admission.</div> : null}
      </div>
    </div>
  );
}